import { endpoints } from '@/services/endpoints';
import { axiosGetRequest, axiosDeleteRequest } from '@/services/Request';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate, Link } from 'react-router';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton';
import AppButton from '@/components/ui/AppButton';
import ReportActions from './TicketComponents/TicketsActionBar';
import BookTicket from './TicketTypes/BookTicket';
import BugTicket from './TicketTypes/BugTicket';
import ChatTicket from './TicketTypes/ChatTicket';
import ReortedUser from './TicketTypes/UserTicket';
import RegularTicket from './TicketTypes/RegularTicket';
import { ITicketData } from '@/types';
import { toast } from 'react-toastify';
import AppBadge from '@/components/ui/AppBade';

export default function TicketDetailPage() {
  const { id } = useParams<{ id: string }>();
  const router = useNavigate();
  const queryClient = useQueryClient();

  const { data: ticket, isLoading } = useQuery({
    queryKey: ['ticketDetail', id],
    queryFn: () => axiosGetRequest(endpoints.getSpecificTicket, { id }),
    select: data => data?.result as ITicketData,
    enabled: !!id,
  });

  const deleteTicket = useMutation({
    mutationFn: () => axiosDeleteRequest(endpoints.deleteTicket, { id: id! }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllTickets] });
      toast.success('Ticket deleted');
      router('/tickets');
    },
    onError: () => toast.error('Failed to delete ticket'),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-48 rounded-lg" />
        <div className="rounded-xl border border-coal-200 bg-white p-6 space-y-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-6 w-full rounded" />
          ))}
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-3">
        <p className="text-base font-medium text-coal-500">Ticket not found</p>
        <Link to="/tickets" className="text-sm text-primary hover:underline">
          ← Back to tickets
        </Link>
      </div>
    );
  }

  const statusColor: Record<string, 'warning' | 'info' | 'success' | 'error'> = {
    pending: 'warning',
    in_progress: 'info',
    resolved: 'success',
    rejected: 'error',
  };

  return (
    <div className="space-y-4">
      {/* Back + meta header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => router('/tickets')}
          className="flex items-center gap-2 rounded-lg border border-coal-200 bg-white px-3 py-1.5 text-sm text-coal-600 transition-colors hover:bg-coal-50"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Tickets
        </button>
        <div className="flex items-center gap-2">
          <span className="text-xs text-coal-400 font-mono">{ticket.ticket_code}</span>
          <AppBadge
            size="small"
            variant="label"
            color={statusColor[ticket.ticket_status] ?? 'warning'}
          >
            {ticket.ticket_status?.replace('_', ' ')}
          </AppBadge>
          <AppBadge size="small" variant="label" color="info">
            {ticket.ticket_type}
          </AppBadge>
        </div>
      </div>

      {/* Action bar (accept / reject) */}
      <div className="rounded-xl border border-coal-200 bg-white overflow-hidden">
        <ReportActions ticket={ticket} />
      </div>

      {/* Ticket content */}
      <div className="rounded-xl border border-coal-200 bg-white overflow-hidden">
        {ticket.ticket_type === 'chat'    && <ChatTicket ticket={ticket} />}
        {ticket.ticket_type === 'book'    && <BookTicket ticket={ticket} />}
        {ticket.ticket_type === 'user'    && <ReortedUser ticket={ticket} />}
        {ticket.ticket_type === 'bug'     && <BugTicket ticket={ticket} />}
        {ticket.ticket_type === 'regular' && <RegularTicket ticket={ticket} />}
      </div>

      {/* Danger zone */}
      <div className="flex justify-end">
        <AppButton
          theme="error"
          size="sm"
          onClick={() => deleteTicket.mutate()}
          loading={deleteTicket.isPending}
        >
          <Trash2 className="h-4 w-4" />
          Delete Ticket
        </AppButton>
      </div>
    </div>
  );
}
